/**
 * Trip Information Page - Mobile-First Trip Briefing
 * Displays customs info, FBO details, passenger count, and operational details
 * Extracted from trip briefing for clean pilot workflow
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getTripById } from '@/services/tripService';
import { authFetch } from '@/services/authService';
import { API_BASE_URL } from '@/services/api';
import { useToast } from '@/contexts/ToastContext';
import type { Trip } from '@/services/tripService';
import {
  InformationCircleIcon,
  ClockIcon,
  UsersIcon,
  MapPinIcon,
  PhoneIcon,
  BuildingOfficeIcon,
  PaperAirplaneIcon,
  ArrowLeftIcon,
  Bars3Icon,
  PlayIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';

export const TripInfoPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showError, showSuccess } = useToast();

  const [trip, setTrip] = useState<Trip | null>(null);
  const [loading, setLoading] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const [sending, setSending] = useState<{ departure: boolean; arrival: boolean }>({
    departure: false,
    arrival: false
  });

  useEffect(() => {
    loadTrip();
  }, [id]);

  const loadTrip = async () => {
    if (!id) return;

    try {
      setLoading(true);
      const data = await getTripById(parseInt(id));
      setTrip(data);
    } catch (err: any) {
      showError('Failed to load trip', err.message || 'Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const sendDepartureNotification = async (flightId: number) => {
    setSending(prev => ({ ...prev, departure: true }));
    try {
      await authFetch(`${API_BASE_URL}/flights/trip/${flightId}/departure-notification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          estimatedEte: '1:30', // You could make this dynamic
          actualDepartureTime: new Date().toLocaleTimeString()
        })
      });

      showSuccess('Departure Notification Sent', 'Ops team has been notified of departure');
    } catch (error: any) {
      showError('Failed to Send Notification', error.message || 'Could not send departure notification');
    } finally {
      setSending(prev => ({ ...prev, departure: false }));
    }
  };

  const sendArrivalNotification = async (flightId: number) => {
    setSending(prev => ({ ...prev, arrival: true }));
    try {
      await authFetch(`${API_BASE_URL}/flights/trip/${flightId}/arrival-notification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          actualArrivalTime: new Date().toLocaleTimeString()
        })
      });

      showSuccess('Arrival Notification Sent', 'Ops team has been notified of arrival');
    } catch (error: any) {
      showError('Failed to Send Notification', error.message || 'Could not send arrival notification');
    } finally {
      setSending(prev => ({ ...prev, arrival: false }));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/50">
        <div className="text-lg">Loading trip information...</div>
      </div>
    );
  }

  if (!trip) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/50">
        <div className="text-lg">Trip not found</div>
      </div>
    );
  }

  const firstFlight = trip.flights[0];
  const lastFlight = trip.flights[trip.flights.length - 1];
  const totalPassengers = trip.flights.reduce((sum, f) => (f.passengers || 0) + sum, 0);

  return (
    <div className="min-h-screen bg-muted/50">
      {/* Header */}
      <div className="bg-card shadow-sm sticky top-0 z-10">
        <div className="flex items-center justify-between p-4">
          <button
            onClick={() => navigate('/dashboard')}
            className="p-2 -ml-2 rounded-lg hover:bg-muted"
          >
            <ArrowLeftIcon className="h-6 w-6" />
          </button>
          <div className="text-center">
            <h1 className="text-lg font-bold">{trip.qNumber}</h1>
            <p className="text-sm text-foreground/80">{trip.aircraftType}</p>
          </div>
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="p-2 -mr-2 rounded-lg hover:bg-muted"
          >
            <Bars3Icon className="h-6 w-6" />
          </button>
        </div>

        {/* Dropdown Menu */}
        {menuOpen && (
          <div className="absolute right-4 top-16 bg-card rounded-lg shadow-lg border w-48 z-20">
            <button
              onClick={() => navigate(`/trips/${trip.id}/wizard`)}
              className="w-full px-4 py-3 text-left hover:bg-muted/50 flex items-center gap-3"
            >
              <PaperAirplaneIcon className="h-5 w-5" />
              Flight Entry
            </button>
            <button
              onClick={() => navigate('/logbook')}
              className="w-full px-4 py-3 text-left hover:bg-muted/50 flex items-center gap-3"
            >
              <InformationCircleIcon className="h-5 w-5" />
              Logbook
            </button>
            <button
              onClick={() => navigate('/pay')}
              className="w-full px-4 py-3 text-left hover:bg-muted/50 flex items-center gap-3"
            >
              <InformationCircleIcon className="h-5 w-5" />
              Pay Summary
            </button>
          </div>
        )}
      </div>

      <div className="max-w-md mx-auto p-4 space-y-4">
        {/* Trip Overview */}
        <div className="bg-card rounded-xl shadow p-6">
          <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
            <InformationCircleIcon className="h-6 w-6 text-blue-600" />
            Trip Overview
          </h2>

          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-foreground/80">Aircraft:</span>
              <span className="font-semibold">{trip.tailNumber}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-foreground/80">Type:</span>
              <span className="font-semibold">{trip.aircraftType}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-foreground/80">Legs:</span>
              <span className="font-semibold">{trip.flights.length}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-foreground/80">Total Passengers:</span>
              <span className="font-semibold">{totalPassengers}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-foreground/80">Status:</span>
              <span className={`px-2 py-1 rounded text-sm font-medium ${
                trip.status === 'IN_PROGRESS'
                  ? 'bg-blue-100 text-blue-800'
                  : trip.status === 'COMPLETED'
                  ? 'bg-green-100 text-green-800'
                  : 'bg-muted text-muted-foreground'
              }`}>
                {trip.status.replace('_', ' ')}
              </span>
            </div>
          </div>
        </div>

        {/* Flight Schedule */}
        <div className="bg-card rounded-xl shadow p-6">
          <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
            <ClockIcon className="h-6 w-6 text-green-600" />
            Flight Schedule
          </h2>

          <div className="space-y-4">
            {trip.flights.map((flight) => (
              <div key={flight.id} className="border-l-4 border-blue-500 pl-4">
                <div className="font-semibold text-lg">
                  Leg {flight.legNumber}: {flight.fromAirport} → {flight.toAirport}
                </div>
                <div className="text-sm text-foreground/80 space-y-1">
                  <div>Date: {new Date(flight.date).toLocaleDateString()}</div>
                  {flight.estimatedDeparture && (
                    <div>ETD: {flight.estimatedDeparture}</div>
                  )}
                  {flight.estimatedArrival && (
                    <div>ETA: {flight.estimatedArrival}</div>
                  )}
                  {flight.passengers && (
                    <div className="flex items-center gap-1">
                      <UsersIcon className="h-4 w-4" />
                      {flight.passengers} passengers
                    </div>
                  )}
                  {flight.distanceNm && (
                    <div>{flight.distanceNm} NM</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Airport Information */}
        <div className="bg-card rounded-xl shadow p-6">
          <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
            <MapPinIcon className="h-6 w-6 text-red-600" />
            Airport Information
          </h2>

          <div className="space-y-6">
            {/* Departure Airport */}
            {firstFlight && (
              <div>
                <h3 className="font-semibold text-lg text-green-700 dark:text-green-400 mb-2">
                  Departure: {firstFlight.fromAirport}
                </h3>
                <div className="bg-muted/50 rounded-lg p-4 space-y-2">
                  <div className="flex items-center gap-2">
                    <BuildingOfficeIcon className="h-4 w-4" />
                    <span className="text-sm">
                      {firstFlight.fromAirport === 'CYHM' && 'Hamilton/John C. Munro'}
                      {firstFlight.fromAirport === 'KTEB' && 'Teterboro Airport'}
                      {firstFlight.fromAirport === 'CYYZ' && 'Toronto Pearson'}
                      {firstFlight.fromAirport === 'CYOW' && 'Ottawa Macdonald-Cartier'}
                      {!['CYHM', 'KTEB', 'CYYZ', 'CYOW'].includes(firstFlight.fromAirport || '') && 'Airport Services'}
                    </span>
                  </div>

                  {firstFlight.fromAirport === 'KTEB' && (
                    <>
                      <div className="text-sm text-blue-600 font-medium">US Customs Information</div>
                      <div className="text-xs text-muted-foreground">
                        • Customs office co-located at Signature Flight Support<br/>
                        • Meet officer at aircraft 30 min prior to departure<br/>
                        • Have crew and passenger passports ready<br/>
                        • Contact: (201) 288-1775
                      </div>
                    </>
                  )}

                  {firstFlight.fromAirport === 'CYHM' && (
                    <>
                      <div className="text-sm text-blue-600 font-medium">Canadian Customs Information</div>
                      <div className="text-xs text-muted-foreground">
                        • CBSA office: Terminal building main floor<br/>
                        • International arrivals processing required<br/>
                        • Contact: (905) 679-4314<br/>
                        • FBO: Skyservice Hamilton - (905) 679-3336
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Arrival Airport */}
            {lastFlight && lastFlight.toAirport !== firstFlight?.fromAirport && (
              <div>
                <h3 className="font-semibold text-lg text-blue-700 dark:text-blue-400 mb-2">
                  Arrival: {lastFlight.toAirport}
                </h3>
                <div className="bg-muted/50 rounded-lg p-4 space-y-2">
                  <div className="flex items-center gap-2">
                    <BuildingOfficeIcon className="h-4 w-4" />
                    <span className="text-sm">
                      {lastFlight.toAirport === 'CYHM' && 'Hamilton/John C. Munro'}
                      {lastFlight.toAirport === 'KTEB' && 'Teterboro Airport'}
                      {lastFlight.toAirport === 'CYYZ' && 'Toronto Pearson'}
                      {lastFlight.toAirport === 'CYOW' && 'Ottawa Macdonald-Cartier'}
                      {!['CYHM', 'KTEB', 'CYYZ', 'CYOW'].includes(lastFlight.toAirport || '') && 'Airport Services'}
                    </span>
                  </div>

                  {lastFlight.toAirport === 'KTEB' && (
                    <>
                      <div className="text-sm text-blue-600 font-medium">US Customs Information</div>
                      <div className="text-xs text-muted-foreground">
                        • Contact CBP on arrival: (201) 288-1775<br/>
                        • Signature Flight Support FBO<br/>
                        • 101 Industrial Avenue, Teterboro, NJ<br/>
                        • Fuel, ground handling available 24/7
                      </div>
                    </>
                  )}

                  {lastFlight.toAirport === 'CYHM' && (
                    <>
                      <div className="text-sm text-blue-600 font-medium">Canadian Customs Information</div>
                      <div className="text-xs text-muted-foreground">
                        • CBSA clearance required for international arrivals<br/>
                        • Skyservice Hamilton FBO<br/>
                        • 9300 Airport Road, Mount Hope, ON<br/>
                        • Contact: (905) 679-3336
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Contact Information */}
        <div className="bg-card rounded-xl shadow p-6">
          <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
            <PhoneIcon className="h-6 w-6 text-purple-600" />
            Important Contacts
          </h2>

          <div className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-foreground/80">Operations:</span>
              <a href="tel:+1-xxx-xxx-xxxx" className="text-blue-600 font-medium">
                (555) 123-4567
              </a>
            </div>
            <div className="flex justify-between">
              <span className="text-foreground/80">Dispatch:</span>
              <a href="tel:+1-xxx-xxx-xxxx" className="text-blue-600 font-medium">
                (555) 123-4568
              </a>
            </div>
            <div className="flex justify-between">
              <span className="text-foreground/80">Maintenance:</span>
              <a href="tel:+1-xxx-xxx-xxxx" className="text-blue-600 font-medium">
                (555) 123-4569
              </a>
            </div>
          </div>
        </div>

        {/* Ops Notifications */}
        <div className="bg-card rounded-xl shadow p-6">
          <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
            <PhoneIcon className="h-6 w-6 text-orange-600" />
            Ops Notifications
          </h2>

          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => sendDepartureNotification(firstFlight?.id || 0)}
              disabled={sending.departure || !firstFlight}
              className="bg-green-600 text-white p-3 rounded-lg font-semibold flex items-center justify-center gap-2 shadow disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <PlayIcon className="h-5 w-5" />
              {sending.departure ? 'Sending...' : 'Departing'}
            </button>

            <button
              onClick={() => sendArrivalNotification(lastFlight?.id || 0)}
              disabled={sending.arrival || !lastFlight}
              className="bg-blue-600 text-white p-3 rounded-lg font-semibold flex items-center justify-center gap-2 shadow disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <CheckCircleIcon className="h-5 w-5" />
              {sending.arrival ? 'Sending...' : 'Arrived'}
            </button>
          </div>

          <p className="text-xs text-foreground/70 mt-3 text-center">
            Send notifications to ops team before takeoff and after landing
          </p>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-2 gap-4">
          <button
            onClick={() => navigate(`/trips/${trip.id}/wizard`)}
            className="bg-blue-600 text-white p-4 rounded-xl font-semibold flex items-center justify-center gap-2 shadow"
          >
            <PaperAirplaneIcon className="h-5 w-5" />
            Flight Entry
          </button>
          <button
            onClick={() => navigate('/dashboard')}
            className="bg-muted text-foreground p-4 rounded-xl font-semibold flex items-center justify-center gap-2 shadow"
          >
            <ArrowLeftIcon className="h-5 w-5" />
            Dashboard
          </button>
        </div>
      </div>
    </div>
  );
};